<template>
  <div class="input" :class="objectClass">
    <span v-if="icon" class="sd-icon" :class="{ clickable: iconClickable }" @click="onIconClick">{{ icon }}</span>
    <input
      v-if="!standaloneError"
      :id="idOrUid"
      class="input__textfield"
      :type="type"
      :name="name"
      :value="value"
      :has-error="onError"
      required="required"
      :placeholder="computedPlaceholder"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      v-on="eventHandlers"
    />
    <label v-if="!standaloneError" class="input__label" :for="idOrUid"><slot /></label>
    <span v-if="unit" class="input__unit">{{ textUnit }}</span>
    <span v-if="sublabel" class="input__sublabel">{{ sublabel }}</span>
    <div v-if="counter" class="input__character-counter">{{ counterValue }}</div>
    <div v-if="onError" class="input__error-message" :class="standAloneClass">{{ errorMessage }}</div>
    <div v-if="helperMessage" class="input__helper-message">{{ helperMessage }}</div>
  </div>
</template>

<script>
import "@smartdesign/core/css/elements/input.css";

export default {
  props: {
    /**
     *  value
     */
    value: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     *  id
     */
    id: {
      required: false,
      type: [Number, String],
      default: undefined,
    },

    /**
     *  name
     */
    name: {
      required: false,
      type: [Number, String],
      default: undefined,
    },

    /**
     *  placeholder
     */
    placeholder: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     * has error state
     */
    hasError: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * error message
     */
    errorMessage: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     * helper message
     */
    helperMessage: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     *  One error message for several inputs
     */
    standaloneError: {
      required: false,
      type: [Boolean],
      default: false,
    },

    /**
     *  disabled
     */
    disabled: {
      required: false,
      type: [Boolean],
      default: false,
    },

    /**
     *  icon
     */
    icon: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     *  icon clickable
     */
    iconClickable: {
      required: false,
      type: [Boolean],
      default: false,
    },

    /**
     *  sublabel
     */
    sublabel: {
      required: false,
      type: [String],
      default: "",
    },

    /**
     *  type of textfield `password`,`tel`,`number`,`email`,`search`,`url`
     */
    type: {
      required: false,
      type: [String],
      default: "text",
      validator(val) {
        return ["text", "password", "tel", "number", "email", "search", "url"].indexOf(val) !== -1;
      },
    },

    /**
     *  inputmode of textfield `none`,`text`,`decimal`,`numeric`
     */
    inputmode: {
      required: false,
      type: String,
      default: "text",
      validator(val) {
        return ["none", "text", "decimal", "numeric"].indexOf(val) !== -1;
      },
    },

    /**
     *  Sizes of unit `100`,`200``300` ([size,unit])
     */
    unit: {
      required: false,
      type: [String],
      default: undefined,
      validator(val) {
        //  component allows unit to be empty, regexp allows them
        return !!/^\s*(100|200|300)\s*,.*$/.exec(val);
      },
    },

    /**
     * Adds the autocomplete to the input field as specified in
     * https://developer.mozilla.org/de/docs/Web/HTML/Element/Input#attr-autocomplete
     * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofilling-form-controls%3A-the-autocomplete-attribute
     */
    autocomplete: {
      required: false,
      type: [String],
      default: null,
    },

    /**
     *  number of characters
     */
    counter: {
      required: false,
      type: [Boolean],
      default: false,
    },

    /**
     *  maxlength of characters
     */
    maxlength: {
      required: false,
      type: [String, Number],
      default: "2500",
      validator: (val) => {
        let maybeNumber = typeof val === "number" ? val : Number.parseInt(val, 10);
        return Number.isInteger(maybeNumber) && maybeNumber > 0;
      },
    },

    /**
     * fieldlength (true, false, `100`, `200` or `300`, default is false)
     */
    fieldlength: {
      required: false,
      type: [Boolean, String, Number],
      default: "",
      validator: (val) => {
        return [true, false, "", "100", "200", "300", 100, 200, 300].indexOf(val) !== -1;
      },
    },
  },

  data() {
    return {
      counterError: null,
      eventHandlers: {
        ...this.$listeners,
        input: (event) => this.updateValue(event.target.value),
      },
    };
  },

  computed: {
    idOrUid() {
      return this.id !== undefined ? this.id : this._uid;
    },

    objectClass() {
      return {
        "input--placeholder": this.placeholder,
        "has-error": this.onError,
        disabled: this.disabled,

        //unit
        "input--unit": this.valueUnit == "100",
        "input--unit input--unit-big": this.valueUnit == "200",
        "input--unit input--unit-extra-big": this.valueUnit == "300",

        //fieldlength
        "input--width-100": this.fieldlength === "100",
        "input--width-200": this.fieldlength === "200",
        "input--width-300": this.fieldlength === "300",
      };
    },

    standAloneClass() {
      return {
        "input__error-message--stand-alone": this.standaloneError,
      };
    },

    computedPlaceholder() {
      if (!this.placeholder && (this.type === "tel" || this.type === "email" || this.type === "url")) {
        return "Placeholder";
      }
      return this.placeholder;
    },

    valueUnit() {
      if (this.unit === undefined) {
        return;
      }
      return this.unit.split(",")[0].trim();
    },

    textUnit() {
      return this.unit.split(",")[1].trim();
    },

    counterValue() {
      return `${this.value.length} / ${this.maxlength} Zeichen`;
    },

    onError() {
      return this.hasError || !!this.counterError;
    },
  },

  methods: {
    countChar(value) {
      this.counterError =
        value.length > this.maxlength ? `Bitte geben Sie maximal ${this.maxlength} Zeichen an.` : null;
    },

    updateValue(value) {
      this.countChar(value);
      this.$emit("input", value);
      if (this.onError !== false) {
        this.$emit("onError", true);
      }
    },

    onIconClick() {
      if (this.iconClickable) {
        this.$emit("icon-click", true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sd-icon.clickable:hover {
  cursor: pointer;
}
</style>
